import {
  ArrowLeftRight,
  BookUser,
  Building2,
  CalendarClock,
  CheckCheck,
  ClipboardCheck,
  Clock,
  CloudDownload,
  FileCheck,
  FileCheck2,
  History,
  IdCard,
  Landmark,
  MapPin,
  MessageSquarePlus,
  MonitorSmartphone,
  Palmtree,
  ServerCog,
  SettingsIcon,
  Tags,
  Truck,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';
import type { NavGroup } from './navItems.js';

/* ===========================================================================
 * THE ADMIN SURFACE'S SECTIONS.
 *
 * Out of `navItems.ts` for the reason `tailwind-nav.ts` is out of the preset
 * and `railSkin.ts` is out of `Sidebar.tsx`: this is a coherent unit with one
 * reason to change — what the Admin panel's back-office rail holds — and the
 * file it came from is long-standing size debt behind a ratchet. `navItems.ts`
 * re-exports `GROUPS`, so every importer and every test is unchanged.
 *
 * The type comes back as a TYPE-ONLY import, so nothing here is a runtime
 * cycle: `import type` is erased entirely.
 * ======================================================================== */

/**
 * Management-console back-office — collapsible, role + feature filtered, and
 * ordered by how often the office actually opens each one:
 *   Approvals (somebody is waiting) → Workforce (people records) →
 *   Time & pay (planning + pay) → Administration (set-up, last).
 *
 * FOUR GROUPS, not eight, and the count is the discipline. The old Approvals /
 * Leave / Time / Payroll groups each held one or two rows after role and
 * feature filtering, so a manager expanded four near-empty sections to find one
 * link; Reports was two rows under a heading of the same name. A section earns
 * its heading by holding rows the reader has to choose between — anything that
 * cannot is a top-level destination (Overview, Operations, the calendar,
 * Reports) or a child of the page it belongs to.
 *
 * The dividing line between here and TOP_LEVEL: these groups are where you go
 * to ACT on records and to configure the tenant. Anything a tenant configures
 * once — reference lists, system tooling, shift definitions — sits in
 * Administration rather than competing with daily work.
 */
export const GROUPS: NavGroup[] = [
  {
    /*
     * THE QUEUES COME FIRST — the only rows here that are somebody waiting.
     *
     * The merged Time & pay note said "action queues (Corrections, Leave
     * approvals) lead", but Corrections was never in the admin surface at all
     * (it lives in the personal list, which admin mode does not render), so the
     * group's first row was Leave approvals and the app's second approval queue
     * could not be reached from the Admin panel.
     */
    id: 'approvals',
    pillar: 'attendance',
    titleKey: 'nav.groups.approvals',
    title: 'Approvals',
    // `Sparkles` is lucide's AI/magic glyph. It said nothing about a queue of
    // people waiting on a decision, and it was drawn on BOTH the section header
    // and one of the rows inside it — so the section and its child were the
    // same picture. A check is what the rows here have in common.
    icon: CheckCheck,
    items: [
      // The approver's queue, not the worker's own request form: `FileSignature`
      // (sign a document) belongs to /corrections in the personal list, which
      // renders alongside this row on a combined-surface console.
      //
      // "Correction APPROVALS", not "Correction requests". Under a heading that
      // says Approvals, three rows read "<thing> approvals" and this one read
      // "<thing> requests" — the worker's word for the object, on the approver's
      // row. One shape for the four (§3.6), and the shape is the one that says
      // what the reader came here to do.
      { to: '/corrections/approvals', labelKey: 'nav.correctionApprovals', label: 'Correction approvals', icon: FileCheck, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'corrections', badge: 'correctionApprovals' },
      // THE QUEUE HALF OF THE LEAVE PAGE. /leave/manage and /leave/calendar are
      // two addresses of one screen now (see the header of LeaveManagePage.tsx),
      // and this row names the one an approver comes for: it opens on the queue,
      // which is what the badge beside it counts. Two rows for one page is
      // usually the defect the device merge removed — it is right here because
      // each view has its OWN PATH, so each row highlights on its own view and
      // only there, which a `?view=` row could never do.
      { to: '/leave/manage', labelKey: 'nav.leaveApprovals', label: 'Leave approvals', icon: ClipboardCheck, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'leave', badge: 'leaveApprovals' },
      // THE FOURTH QUEUE, and until now the only one with no door on this
      // surface. A letter request is decided by the same three roles that
      // decide the two rows above, and the queue existed — `LetterApprovalsPanel`
      // behind `?tab=queue`, counted by the dashboard's own decision card — but
      // `/letters` is a PERSONAL row, and admin mode renders no personal rows.
      // So an approver working the Admin panel could reach three of the app's
      // four queues.
      //
      // It points at `/letters/approvals` rather than `?tab=queue` for the
      // reason the Devices note below spells out: `pathMatches` compares `to`
      // against `pathname` with no query awareness, so a query-string row could
      // never highlight. Corrections solved this the same way and this is that
      // solution applied, not a new pattern.
      { to: '/letters/approvals', labelKey: 'nav.letterApprovals', label: 'Letter approvals', icon: FileCheck2, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'letters', badge: 'letterApprovals' },
      // THE FIFTH QUEUE, and the one whose absence was worst. The ERP exchange
      // ran BOTH WAYS with nobody in it: a scheduled job wrote the file that
      // pays about a thousand people, and a nightly job rewrote payroll routing
      // from whatever workbook was in a folder. Both now wait on a person, and
      // this is where that person looks.
      //
      // ADMIN/HR, unlike the four rows above it. Leave, corrections and letters
      // are decided by whoever has authority over a WORKER, which is what makes
      // a MANAGER an approver there. Neither ERP object belongs to a team: one
      // is the whole workforce's month, the other is how everybody is paid.
      //
      // Points at the OUTBOUND half, because that is the one with a deadline —
      // the export fires on a configured day and withholds an unapproved batch.
      // An inbound change waits patiently; a payroll file does not.
      { to: '/erp/approvals', labelKey: 'nav.erpApprovals', label: 'ERP approvals', icon: ArrowLeftRight, roles: ['ADMIN', 'HR'], badge: 'erpChanges' },
      // THE DEVICE QUEUE, back under Approvals — every queue under one
      // heading, matching the handoff. It used to be filed under
      // Administration on the reasoning that a rail row names its
      // DESTINATION rather than what's inside it, and the destination is a
      // register that CONTAINS a queue rather than being one. Restored here
      // on the owner's call that "where an approver looks for waiting work"
      // outweighs that distinction — Correction requests and Leave approvals
      // are exactly this shape already (a queue view of a bigger page), so
      // Devices being the same shape is consistent, not an exception.
      //
      // This MOVES the one existing row, it does not add a second one: there
      // is still exactly one `/settings/devices` destination and one row
      // pointing at it, just filed here instead of under Administration. Two
      // rows at that path was the thing ruled out — `pathMatches` compares
      // `to` against `pathname` with no query awareness, so a second row at
      // the same bare path would highlight alongside this one on every visit,
      // and a `?status=PENDING` variant would never highlight at all.
      // NAMED FOR THE QUEUE, like its two siblings. The handoff calls this row
      // "Device approvals" (`Hadir Dashboard.dc.html:95`), and under an
      // Approvals heading that is what an approver is looking for. It differs
      // from the page's own "Devices" heading — but so do the two rows above
      // it ("Correction requests" opens *Corrections*, "Leave approvals" opens
      // *Leave*), because in every case the rail names the QUEUE and the page
      // names the REGISTER that contains it. The page keeps "Devices" because
      // it is also a Settings destination, where the register is the point.
      { to: '/settings/devices', labelKey: 'nav.devices', label: 'Device approvals', icon: MonitorSmartphone, roles: ['ADMIN', 'HR'], badge: 'deviceApprovals' },
    ],
  },
  {
    id: 'workforce',
    pillar: 'people',
    titleKey: 'nav.groups.workforce',
    title: 'Workforce',
    icon: Users,
    items: [
      { to: '/employees', labelKey: 'nav.employees', label: 'Employees', icon: Users, roles: ['ADMIN', 'HR', 'MANAGER'] },
      // BULK IMPORT IS NOT HERE ANY MORE — it is a child of the row above, and
      // the rail was drawing it as that row's sibling. `/employees/import` is
      // reached from the Employees page's own header action, which is where a
      // reader is standing when they decide to load a spreadsheet of people:
      // nobody opens the sidebar to import, they open the list, see it is
      // empty, and import. Listing it here made the flat rail claim a
      // hierarchy the routes contradict, and put a once-a-quarter task beside
      // the app's most-opened screen. It stays in EXTRA_DESTINATIONS so ⌘K
      // still jumps straight to it.
      { to: '/worksites', labelKey: 'nav.worksites', label: 'Worksites', icon: MapPin, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'workers' },
      // These two were adjacent rows drawn with the SAME glyph — legible only
      // because the labels were there to tell them apart, which is precisely
      // what the collapsed rail takes away. A directory is a book of people; a
      // team's attendance is a person with a tick.
      { to: '/team', labelKey: 'nav.teamDirectory', label: 'Team directory', icon: BookUser, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'team' },
      { to: '/team-attendance', labelKey: 'nav.teamAttendance', label: 'Team attendance', icon: UserCheck, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'team' },
      /* SCHEDULE AND PAYROLL PREP MOVED HERE, and "Time & pay" is gone.
         That section held three rows: the leave calendar, the schedule and
         payroll prep. The calendar row is retired — it is one VIEW of the leave
         approvals screen, which carries a two-tab strip, so the row was a
         second address for a tab. What was left could not stand up: for a
         MANAGER payroll is ADMIN/HR-only, so the section became a heading, a
         chevron and one link — the exact decay `navItems.test.ts` guards, and
         the reason the calendar row had been kept until now.

         So the SECTION went instead of the row. Both survivors are about the
         subject this group already holds: who your people are, where they work,
         WHEN they work, and what they are paid. Three groups now, and the count
         is still the discipline — a section earns its heading by holding rows
         the reader has to choose between. */
      { to: '/schedule', labelKey: 'nav.schedule', label: 'Schedule', icon: CalendarClock, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'shifts' },
      { to: '/payroll', labelKey: 'nav.payrollPrep', label: 'Payroll prep', icon: Wallet, roles: ['ADMIN', 'HR'], feature: 'payroll' },
      /* THE AUTHORING DOOR ON THE ANNOUNCEMENTS SCREEN, and it was missing.
         
         An HR user published an announcement, saw it render as a banner on the
         Overview, and had no row anywhere in the Admin panel that led back to
         the screen which manages it. `AnnouncementsPage` is role-adapted and
         has been all along — the composer, the audience picker and the manage
         feed are already there for ADMIN/HR — so the only thing absent was a
         door. An authoring surface reachable only by typing its URL is a nav
         defect, not a missing feature.
         
         It points at `/announcements/manage`, a second role-gated PATH onto the
         same component (App.tsx), for the reason the letters queue is at
         `/letters/approvals`: `/announcements` is a PERSONAL row, admin mode
         renders no personal rows, and re-pointing an admin row at the bare path
         would make `isAdminPath()` read it as a management destination — which
         would drop an ordinary worker into the Admin panel the moment they
         opened their own Announcements row.
         
         WORKFORCE, NOT ADMINISTRATION. The line this file draws is between
         acting on records and configuring the tenant once; publishing to the
         workforce is a recurring act aimed at the people this group is about,
         and filing it beside Leave types and Holidays would put a weekly task
         among the reference lists nobody opens twice.
         
         NAMED FOR THE JOB, like every other admin row whose page also has a
         personal one. "Correction approvals" opens *Corrections* and "Letter
         approvals" opens *Letters*; this opens *Announcements* and says what
         the reader came to do. It also cannot be confused with the personal
         reading row on a combined console, where both render — and its glyph
         differs from that row's megaphone for the same reason, because
         collapsed the rail is nothing but glyphs. */
      { to: '/announcements/manage', labelKey: 'nav.manageAnnouncements', label: 'Manage announcements', icon: MessageSquarePlus, roles: ['ADMIN', 'HR'], feature: 'announcements' },
    ],
  },
  {
    // Everything you configure once and rarely touch: settings, the reference
    // lists (departments/designations/leave types/holidays) and system tooling.
    // Deliberately LAST.
    //
    // It used to claim "and collapsed by default", which stopped being true when
    // the Sidebar moved every group to open-by-default (see the note there —
    // three of the app's commonest tasks were paying a click to reveal a
    // heading's children). A comment describing behaviour the code no longer has
    // is the same defect as a screen describing data it no longer holds.
    id: 'administration',
    pillar: 'admin',
    titleKey: 'nav.groups.administration',
    title: 'Administration',
    // The COG, not the wrench. A section heading is the name of what is under
    // it, and this section's anchor row is Settings — the same relationship
    // Workforce has to Employees and Time & pay to Shifts. The wrench said
    // "repairs", which is not what a tenant comes here to do.
    icon: SettingsIcon,
    items: [
      { to: '/settings', labelKey: 'nav.settings', label: 'Settings', icon: SettingsIcon, roles: ['ADMIN', 'HR'] },
      { to: '/departments', labelKey: 'nav.departments', label: 'Departments', icon: Building2, roles: ['ADMIN', 'HR'], feature: 'orgStructure' },
      { to: '/designations', labelKey: 'nav.designations', label: 'Designations', icon: IdCard, roles: ['ADMIN', 'HR'], feature: 'orgStructure' },
      { to: '/branches', labelKey: 'nav.branches', label: 'Company branches', icon: Landmark, roles: ['ADMIN', 'HR'] },
      { to: '/suppliers', labelKey: 'nav.suppliers', label: 'Labour suppliers', icon: Truck, roles: ['ADMIN', 'HR'] },
      { to: '/leave/types', labelKey: 'nav.leaveTypes', label: 'Leave types', icon: Tags, roles: ['ADMIN', 'HR'], feature: 'leave' },
      // The shift DEFINITIONS, beside the other reference lists — see the note
      // where this row used to sit, under Time & pay.
      { to: '/shifts', labelKey: 'nav.shifts', label: 'Shifts', icon: Clock, roles: ['ADMIN', 'HR'], feature: 'shifts' },
      { to: '/holidays', labelKey: 'nav.holidays', label: 'Holidays', icon: Palmtree, roles: ['ADMIN', 'HR'], feature: 'leave' },
      // THE DEVICE REGISTER ROW MOVED TO APPROVALS (see the note there) — this
      // group no longer names it. `MonitorSmartphone` and the pending badge
      // both travelled with it; there is still exactly one row and one
      // `/settings/devices` destination, only its section changed.
      { to: '/system/audit-log', labelKey: 'nav.auditLog', label: 'Audit log', icon: History, roles: ['ADMIN'] },
      { to: '/system/jobs', labelKey: 'nav.jobs', label: 'Jobs', icon: ServerCog, roles: ['ADMIN'] },
      // SHIPPING THE FIELD APK. Filed here rather than beside the device
      // register under Approvals, because the destination is not a device: it
      // is a BUILD, and it exists whether or not one tablet is registered.
      // Its two neighbours are the right ones — the audit log and the job queue
      // are the other two ADMIN-only screens that operate on the estate rather
      // than configure the tenant.
      //
      // `CloudDownload` and not `MonitorSmartphone`: the rail collapses to
      // glyphs alone, and a second tablet-shaped picture two rows from the
      // device register is two rows nobody can tell apart. What happens here is
      // a build coming down to the fleet.
      { to: '/system/field-app', labelKey: 'nav.fieldApp', label: 'Field app updates', icon: CloudDownload, roles: ['ADMIN'] },
    ],
  },
];
